import {Modal} from '@mantine/core';
import {Dispatch, FC, SetStateAction, useCallback, useMemo} from 'react';
import {SubmitHandler} from 'react-hook-form';
import {useAsync, useAsyncFn} from 'react-use';
import {CodesService} from '../../api/generated/CodesService';
import {DocumentsService} from '../../api/generated/DocumentsService';
import {FilesService} from '../../api/generated/FilesService';
import {AsyncStateContainer} from '../../components/async-state-container';
import {createForm} from '../../forms';
import {useNotification} from '../../hooks/use-notifications';
import {useOptions} from '../../hooks/use-options';
import {notify} from '../../hooks/use-subscription';
import {useJobStore} from '../job-store';
import {DocumentSchema, documentSchema} from './schemas';
import {extractFileName} from '../../forms/adapters/file-button';

const DocumentForm = createForm<DocumentSchema>();

type DocumentCreateProps = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  jobId: number;
  reviewId?: number;
};

export const DocumentCreate: FC<DocumentCreateProps> = ({
  open,
  setOpen,
  jobId,
  reviewId,
}) => {
  const notifications = useNotification();
  const {fetchJob} = useJobStore((state) => ({
    fetchJob: state.fetchJob,
  }));

  const defaultValues: DocumentSchema = useMemo(
    () => ({
      jobId,
      reviewId,
      name: '',
      url: '',
    }),
    [jobId, reviewId],
  );

  const onSubmit = useCallback<SubmitHandler<DocumentSchema>>(
    async (value) => {
      if (!value.name) value.name = extractFileName(value.url);
      const response = await DocumentsService.create({body: value});

      if (response.hasErrors) {
        return response;
      }

      notifications.success('Document Created');
      notify('document-listing', undefined);
      await fetchJob(jobId);
      setOpen(false);
    },
    [fetchJob, jobId, notifications, setOpen],
  );

  return (
    <Modal
      size='xl'
      title='Create Document'
      opened={open}
      onClose={() => setOpen(false)}
    >
      <DocumentForm
        defaultValues={defaultValues}
        schema={documentSchema}
        onSubmit={onSubmit}
      >
        {({formState: {isSubmitting, isLoading, isValidating}}) => (
          <DocumentFormFields
            loading={isSubmitting || isLoading || isValidating}
          />
        )}
      </DocumentForm>
    </Modal>
  );
};

type DocumentUpdateProps = {
  id: number | undefined;
  jobId: number | undefined;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
};

export const DocumentUpdate: FC<DocumentUpdateProps> = ({
  id,
  jobId,
  open,
  setOpen,
}) => {
  const notifications = useNotification();
  const {fetchJob} = useJobStore((state) => ({
    fetchJob: state.fetchJob,
  }));

  const [fetchDocumentState, fetchDocument] = useAsyncFn(async () => {
    if (!id) return;
    const {data} = await DocumentsService.getById({
      id,
    });
    return data;
  }, [id]);

  const document = fetchDocumentState.value || undefined;

  const onSubmit = useCallback<SubmitHandler<DocumentSchema>>(
    async (value) => {
      if (!value.name) value.name = extractFileName(value.url);
      if (!id) return;
      const response = await DocumentsService.update({id, body: value});

      if (response.hasErrors) {
        return response;
      }

      notifications.success('Document Updated');
      notify('document-listing', undefined);
      jobId && (await fetchJob(jobId));
      await fetchDocument();
      setOpen(false);
    },
    [fetchDocument, fetchJob, id, jobId, notifications, setOpen],
  );

  useAsync(async () => await fetchDocument(), [fetchDocument]);

  return (
    <Modal title='Update Document' opened={open} onClose={() => setOpen(false)}>
      <AsyncStateContainer {...fetchDocumentState}>
        <DocumentForm
          defaultValues={document}
          schema={documentSchema}
          onSubmit={onSubmit}
        >
          {({formState: {isSubmitting, isLoading, isValidating}}) => (
            <DocumentFormFields
              loading={isSubmitting || isLoading || isValidating}
            />
          )}
        </DocumentForm>
      </AsyncStateContainer>
    </Modal>
  );
};

const DocumentFormFields: FC<{loading?: boolean}> = ({loading}) => {
  const notifications = useNotification();
  const codeOptions =
    useOptions(CodesService.getDocumentTypeCodeOptions).value ?? [];

  const onDrop = useCallback(
    async (file: File | null) => {
      if (!file) {
        return;
      }

      const response = await FilesService.upload({file});

      if (response.hasErrors) {
        notifications.errors(response.errors);
        return;
      }

      return response.data?.uri;
    },
    [notifications],
  );

  return (
    <>
      <DocumentForm.Row>
        <DocumentForm.TextInput name='name' label='Name' />
      </DocumentForm.Row>
      <DocumentForm.Row mt={14}>
        <DocumentForm.Select
          maxDropdownHeight={112}
          name='codeId'
          label='Type'
          data={codeOptions}
        />
      </DocumentForm.Row>
      <DocumentForm.Row mt={64} mb={8}>
        <DocumentForm.FileButton name='url' onChange={onDrop} />
      </DocumentForm.Row>
      <DocumentForm.Row mt={20}>
        <DocumentForm.Submit loading={loading}>Submit</DocumentForm.Submit>
      </DocumentForm.Row>
    </>
  );
};
